// FK-driven JOIN snippet generator for the IntelliSense orchestrator.
// PRD: Slice "FK-Driven JOIN Snippet Infrastructure". Pure module — no
// Monaco runtime dep, no I/O. Per-engine FK introspection lives in each
// `… — Joins + FK` sub-slice; this module turns a single `FkEdge` plus the
// surrounding cursor scope into a `CompletionCandidate` whose `insertText`
// is a Monaco snippet that drops a fully-formed JOIN clause at the cursor.
//
// Direction handling: an FkEdge is asymmetric (`fromTable.fromColumns →
// toTable.toColumns`). A user could be JOINing in either direction —
// `from = OrderHeader, candidate = Customer` (forward) or
// `from = Customer, candidate = OrderHeader` (reverse). The generator picks
// up on whichever side the candidate sits and swaps the column lists so the
// emitted ON clause always reads `${candidate}.col = ${from.alias}.col`.
//
// Multi-FK disambiguation: callers iterate `findApplicableFks` and call
// `generateFkJoinSnippet` once per edge. The `detail` field carries the
// FK identity (column list + name) so the user can pick between e.g.
// `BillToCustomerId` and `ShipToCustomerId` joins between the same pair
// of tables.
//
// Spelling: the candidate table and every FK column are CATALOG spellings
// (schema snapshot, FK edge, MetaInfo declaration), so they are written
// through `catalogIdentSql` — bare only when the engine reads the bare text
// back to exactly those bytes (`"Orders"."CustomerId"` on PostgreSQL,
// `[Order Details]` on SQL Server). The bound side's qualifier is the query's
// own spelling (`boundAliasSql`).

import type {
  AliasBinding,
  CompletionCandidate,
  DatabaseEngineId,
  FkEdge,
  TableMeta,
} from '../../../core/engines';
import { databaseEngineIdToSqlEngine } from '../../../core/engines/databaseEngineIdToSqlEngine';
import { catalogIdentSql } from '../../sql-resolver/suggest/sqlIdentifierText';

import { buildFkEdgeIdentityIndex, buildFkEdgeIndex } from './fkEdgeIndex';
import type { FkEndpointMatcher } from './fkEndpointMatch';

export type FkJoinSnippetInput = {
  /** The relation already FROM'd (or JOINed) that the new JOIN attaches to. */
  readonly from: AliasBinding;
  /** The candidate destination table the user is JOINing to. */
  readonly candidate: TableMeta;
  /** A single FK edge linking `from` and `candidate` in either direction. */
  readonly fk: FkEdge;
  /** Aliases already bound in the surrounding scope (lower-cased compare). */
  readonly existingAliases: ReadonlySet<string>;
  /** The dialect the snippet is written in — catalog names are delimited by its rules. */
  readonly engine: DatabaseEngineId;
  /**
   * Relation identity for the direction test (`fkEndpointMatch.ts`) — the
   * resolvers pass it; without one the edge's names are matched folded and a
   * missing schema matches any (the pure-helper behaviour).
   */
  readonly match?: FkEndpointMatcher;
  /**
   * Write the candidate without its schema (it resolves bare — PostgreSQL's
   * search_path tables). `candidate.schema` stays its identity for `match`.
   */
  readonly bareRelation?: boolean;
};

/**
 * Produce a tier-1 snippet candidate that inserts the relation name + a
 * tab-stopped alias + the resolved ON clause for a single FK edge.
 *
 * The candidate's `insertText` does NOT include the leading `JOIN` keyword —
 * callers fire this generator only when the cursor is in `'from'` position
 * after a JOIN keyword has already been typed (or when the user has invoked
 * the snippet alongside an explicit JOIN keyword the orchestrator already
 * inserted). The label and `filterText` use the bare relation name so
 * Monaco's fuzzy match still surfaces the snippet for prefixes like `Cust`.
 *
 * Throws when `fk` does not connect `from.relation` to `candidate.name` in
 * either direction — caller bug, not a runtime path.
 */
export function generateFkJoinSnippet(input: FkJoinSnippetInput): CompletionCandidate {
  const { from, candidate, fk, existingAliases, engine, match } = input;
  const direction = determineFkDirection(from, candidate, fk, match);
  if (!direction) {
    throw new Error(
      `FK does not connect "${qualifiedName(from.schema, from.relation)}" ` +
        `to "${qualifiedName(candidate.schema, candidate.name)}"`,
    );
  }
  const { candidateCols, fromCols } = direction;
  // An FK's two column lists must be equal-length (each candidate column
  // pairs with exactly one from-side column). A malformed/asymmetric edge
  // would otherwise zip past the shorter list and emit a literal
  // `${alias}.undefined` into the snippet. Refuse to emit a broken join —
  // same fail-loud contract as the no-direction throw above; the sole
  // caller path (resolver → orchestrator → SqlMonaco) catches this and
  // simply skips the FK candidate.
  if (candidateCols.length !== fromCols.length) {
    throw new Error(
      `FK column arity mismatch for "${qualifiedName(from.schema, from.relation)}" ↔ ` +
        `"${qualifiedName(candidate.schema, candidate.name)}" ` +
        `(candidateCols=${candidateCols.length}, fromCols=${fromCols.length}) — ` +
        `cannot generate a valid join`,
    );
  }
  const newAlias = generateShortAlias(candidate.name, existingAliases);
  const ident = catalogIdentWriter(engine);
  const relation = catalogTableSql(input.bareRelation ? { name: candidate.name } : candidate, engine);
  // The bound side as the query spelled it — `"order items"`, `[o]`, `"Ord"`:
  // the parsed `alias` has its delimiters stripped and, bare, names nothing.
  const fromQualifier = boundAliasSql(from);

  const onClauseInsert = candidateCols
    .map((col, i) => `\${1}.${ident(col)} = ${fromQualifier}.${ident(fromCols[i]!)}`)
    .join(' AND ');
  const insertText = `${relation} \${1:${newAlias}} ON ${onClauseInsert}$0`;

  const onClauseLabel = candidateCols
    .map((col, i) => `${newAlias}.${ident(col)} = ${fromQualifier}.${ident(fromCols[i]!)}`)
    .join(' AND ');
  const label = `${relation} ON ${onClauseLabel}`;

  const colSummary = candidateCols.join(', ');
  const detail = fk.name ? `FK: ${colSummary} → ${fk.name}` : `FK: ${colSummary}`;

  return {
    label,
    kind: 'snippet',
    tier: 1,
    insertText,
    isSnippet: true,
    detail,
    filterText: candidate.name,
    fkJoin: true,
    fkJoinFromAlias: from.alias,
  };
}

/**
 * Returns every FK edge in `allEdges` that connects the two tables in
 * either direction. With `match` (the resolvers), relation identity decides:
 * the edge's endpoints and the bound relation resolve to snapshot tables
 * (`fkEndpointMatch.ts`). Without it, names are compared case-insensitively
 * and an undefined schema on either side is treated as "any" so dialects
 * without first-class schemas (SQLite, MySQL when database == schema) still
 * surface FK snippets.
 *
 * A linear scan — callers asking for many (binding, candidate) pairs over
 * the same edge list use {@link makeApplicableFksFinder} instead.
 */
export function findApplicableFks(
  from: AliasBinding,
  candidate: TableMeta,
  allEdges: readonly FkEdge[],
  match?: FkEndpointMatcher,
): readonly FkEdge[] {
  return allEdges.filter((fk) => determineFkDirection(from, candidate, fk, match) !== null);
}

/**
 * {@link findApplicableFks} for many lookups over ONE edge list during one
 * synchronous pass: indexes the edges by table once (`fkEdgeIndex.ts` — by
 * the tables their endpoints name when `match` is given, else by name) and
 * scans only the edges touching each candidate — same result, same order.
 * Create it where the pass starts and drop it when the pass ends; never
 * keep it across requests (snapshot FK arrays are refilled in place).
 */
export function makeApplicableFksFinder(
  allEdges: readonly FkEdge[],
  match?: FkEndpointMatcher,
): (from: AliasBinding, candidate: TableMeta) => readonly FkEdge[] {
  // With relation identity, only the edges whose endpoints name this very
  // table (not every same-named table in another schema) are tested.
  const byIdentity = match ? buildFkEdgeIdentityIndex(allEdges, match) : undefined;
  const byName = byIdentity ? undefined : buildFkEdgeIndex(allEdges);
  const edgesTouching = (candidate: TableMeta) => (byIdentity ? byIdentity(candidate) : byName!(candidate.name));
  return (from, candidate) =>
    edgesTouching(candidate).filter((fk) => determineFkDirection(from, candidate, fk, match) !== null);
}

/**
 * Generate a short alias for `tableName`: first character + every subsequent
 * uppercase character, lower-cased. `OrderHeader` → `oh`, `Customer` → `c`,
 * `salesperson_history` → `s`. Falls back to `t` when `tableName` is empty.
 *
 * If the resulting alias is already in `existingAliases` (case-insensitive),
 * appends `2`, `3`, ... until an unused name is found. Caps at 99 to avoid a
 * pathological loop in the unlikely event the scope already has 100+ aliases
 * sharing the same base.
 */
export function generateShortAlias(
  tableName: string,
  existingAliases: ReadonlySet<string>,
): string {
  let base = '';
  if (tableName.length > 0) base = tableName[0]!.toLowerCase();
  for (let i = 1; i < tableName.length; i++) {
    const ch = tableName[i]!;
    if (ch >= 'A' && ch <= 'Z') base += ch.toLowerCase();
  }
  if (base.length === 0) base = 't';

  const lowerExisting = new Set<string>();
  for (const a of existingAliases) lowerExisting.add(a.toLowerCase());

  if (!lowerExisting.has(base)) return base;
  for (let i = 2; i < 100; i++) {
    const next = `${base}${i}`;
    if (!lowerExisting.has(next)) return next;
  }
  return base;
}

type FkDirection = {
  /** Columns on the candidate (destination) side — used in `${1}.col`. */
  readonly candidateCols: readonly string[];
  /** Columns on the from-binding side — used in `${from.alias}.col`. */
  readonly fromCols: readonly string[];
  /** The candidate is the edge's FROM (referencing) side — the forward direction. */
  readonly candidateIsSource: boolean;
};

/**
 * Whether `candidate` is the referencing (FROM) side of `fk` in the direction
 * `generateFkJoinSnippet` joins it — relation identity with schemas, the same
 * test its ON equality uses (so `a.nodes → b.nodes` is told apart from its
 * reverse even though both table names match both ways); `null` when the
 * edge does not connect the two. Loose-FK filters follow this direction.
 */
export function fkCandidateIsSource(
  from: AliasBinding,
  candidate: TableMeta,
  fk: FkEdge,
  match?: FkEndpointMatcher,
): boolean | null {
  return determineFkDirection(from, candidate, fk, match)?.candidateIsSource ?? null;
}

function determineFkDirection(
  from: AliasBinding,
  candidate: TableMeta,
  fk: FkEdge,
  match?: FkEndpointMatcher,
): FkDirection | null {
  if (match) return determineFkDirectionByIdentity(from, candidate, fk, match);
  const fromName = from.relation.toLowerCase();
  const fromSchema = from.schema?.toLowerCase();
  const candName = candidate.name.toLowerCase();
  const candSchema = candidate.schema?.toLowerCase();

  // Forward: edge points candidate.fromColumns → from.toColumns
  if (
    fk.fromTable.toLowerCase() === candName &&
    schemaMatches(fk.fromSchema, candSchema) &&
    fk.toTable.toLowerCase() === fromName &&
    schemaMatches(fk.toSchema, fromSchema)
  ) {
    return { candidateCols: fk.fromColumns, fromCols: fk.toColumns, candidateIsSource: true };
  }
  // Reverse: edge points from.fromColumns → candidate.toColumns
  if (
    fk.fromTable.toLowerCase() === fromName &&
    schemaMatches(fk.fromSchema, fromSchema) &&
    fk.toTable.toLowerCase() === candName &&
    schemaMatches(fk.toSchema, candSchema)
  ) {
    return { candidateCols: fk.toColumns, fromCols: fk.fromColumns, candidateIsSource: false };
  }
  return null;
}

/**
 * {@link determineFkDirection} by relation identity: the bound relation is the
 * table the engine resolves it to (none → the edge does not apply), and each
 * endpoint must name that exact table / the candidate (`fkEndpointMatch.ts`).
 */
function determineFkDirectionByIdentity(
  from: AliasBinding,
  candidate: TableMeta,
  fk: FkEdge,
  match: FkEndpointMatcher,
): FkDirection | null {
  const bound = match.bindingTable(from);
  if (!bound) return null;
  if (match.endpointIs(fk.fromSchema, fk.fromTable, candidate) && match.endpointIs(fk.toSchema, fk.toTable, bound)) {
    return { candidateCols: fk.fromColumns, fromCols: fk.toColumns, candidateIsSource: true };
  }
  if (match.endpointIs(fk.fromSchema, fk.fromTable, bound) && match.endpointIs(fk.toSchema, fk.toTable, candidate)) {
    return { candidateCols: fk.toColumns, fromCols: fk.fromColumns, candidateIsSource: false };
  }
  return null;
}

/**
 * Schema equality with "any" semantics: equal (case-insensitively) when both
 * sides are present; a match when either side is absent so dialects without
 * first-class schemas (SQLite, MySQL when database == schema) still surface
 * snippets. Shared with the JSON-path loose-FK builder for identical
 * direction-matching semantics.
 */
export function schemaMatches(
  a: string | undefined,
  b: string | undefined,
): boolean {
  if (a === undefined || b === undefined) return true;
  return a.toLowerCase() === b.toLowerCase();
}

function qualifiedName(schema: string | undefined, name: string): string {
  return schema ? `${schema}.${name}` : name;
}

/**
 * Writes a catalog-held name (a table, schema or column of the snapshot, an FK
 * edge or a MetaInfo declaration) as SQL for `engine`: bare when that is
 * exact, else delimited. Engines without a grammar mapping (`mock`, `kusto`)
 * keep the name as is. Shared with the loose-FK builders.
 */
export function catalogIdentWriter(engine: DatabaseEngineId): (name: string) => string {
  const sqlEngine = databaseEngineIdToSqlEngine(engine);
  return sqlEngine ? (name) => catalogIdentSql(name, sqlEngine) : (name) => name;
}

/** The candidate table as SQL: `schema.name`, each part through {@link catalogIdentWriter}. */
export function catalogTableSql(table: Pick<TableMeta, 'name' | 'schema'>, engine: DatabaseEngineId): string {
  const ident = catalogIdentWriter(engine);
  return table.schema ? `${ident(table.schema)}.${ident(table.name)}` : ident(table.name);
}

/**
 * A bound relation's qualifier as insert text: its VERBATIM source spelling
 * (`AliasBinding.aliasSql`, delimiters included), falling back to the parsed
 * alias for producers that predate it. Shared with the loose-FK builders.
 */
export function boundAliasSql(binding: AliasBinding): string {
  return binding.aliasSql ?? binding.alias;
}
